import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DriverReferralCampaignView, DriverReferralCeilingView, ReferralCampaignView, ResolvedCatalogCategoryView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  EMPTY_DRIVER_REFERRAL_DRAFT,
  EMPTY_RIDER_REFERRAL_DRAFT,
  committedBp,
  draftFaceMinor,
  driverCampaignCanChange,
  driverCampaignCanEnd,
  driverCampaignDecisionBlock,
  driverCampaignTone,
  driverChangeDecisionBlock,
  driverReferralDraftIssue,
  driverReferralProposalBody,
  formatMarketInstant,
  marketTimeZone,
  referralPercentLabel,
  referralAuthority,
  riderDeadlineAfter,
  rewardsCovered,
  riderReferralBody,
  riderReferralDraftComplete,
  tailOf,
  type DriverReferralDraft,
  type RiderReferralDraft,
} from '../../src/index';
import { useAdmin } from '../session';
import { usePersonNames } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, Button, EmptyState, Field, FormRow, Meter, MoneyInput, Notice, PageHeader, Panel, Row, Select, Stack, Table, Text, TextInput, useAutoRefresh } from '@vello/web-theme';

/**
 * Indicações (D723, doc 29 gap G12, owner Q7) - the referral campaigns of the market in the header scope (never typed):
 *
 * - **Convide motoristas** (`DriverReferralAdminApi`, `/v1/driver-referral-campaigns/*`): the governed ceiling in force (`promotion.driverReferral` - the reason and
 *   version that decided it - and the coupon envelope its reward is paid from); the campaigns per category with the reward exactly as configured, the window in the
 *   market's own zone, how much of the budget the referrals hold, the state and who proposed; FOUR EYES - the maker proposes (a category from the market's own catalog,
 *   money through `MoneyInput` in the envelope's currency, every number bounded by the ceiling), a SECOND operator approves or refuses (hidden from the proposer, with
 *   the reason shown); a change (deadline, budget, cap) is proposed and approved the same way; any operator holding the word ends an active one.
 * - **Indique um amigo** (`CouponAdminApi`, `/v1/referral-campaigns`): the rider-to-rider campaigns (D476) - listed, created, switched off and on, and their deadline
 *   changed. These are still ONE operator's act (the D476 backend has no approval step - a recorded follow-up); the panel says so instead of implying four eyes.
 * A refused or failed read is an ERROR, never an empty list; every code shown is read through the central vocabulary; no native dialog is ever opened.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;

const note = (text: string): ReactElement => <EmptyState title={text} />;
const intOf = (value: string): number | undefined => (value.trim() === '' || !/^\d+$/.test(value.trim()) ? undefined : Number.parseInt(value.trim(), 10));

/** The category's label in the console's language (exact tag, then the language's prefix), else its code. */
function categoryLabel(c: ResolvedCatalogCategoryView | undefined, code: string, lang: string): string {
  if (c === undefined) return code;
  const exact = c.labels[lang];
  if (exact !== undefined) return exact;
  const prefixed = Object.entries(c.labels).find(([tag]) => tag.split('-')[0] === lang);
  return prefixed?.[1] ?? code;
}

export function ReferralCampaignsScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const { mayView, mayManage } = referralAuthority(grants);
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [loaded, setLoaded] = useState('');
  const [ceiling, setCeiling] = useState<DriverReferralCeilingView | undefined>(undefined);
  const [campaigns, setCampaigns] = useState<readonly DriverReferralCampaignView[] | undefined>(undefined);
  const [riderCampaigns, setRiderCampaigns] = useState<readonly ReferralCampaignView[] | undefined>(undefined);
  const [categories, setCategories] = useState<readonly ResolvedCatalogCategoryView[] | undefined>(undefined);
  const [categoriesFailed, setCategoriesFailed] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [changes, setChanges] = useState<Record<string, { days?: string; cap?: string; budget?: bigint }>>({});
  const [riderDays, setRiderDays] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<DriverReferralDraft>(EMPTY_DRIVER_REFERRAL_DRAFT);
  const [riderDraft, setRiderDraft] = useState<RiderReferralDraft>(EMPTY_RIDER_REFERRAL_DRAFT);
  const personName = usePersonNames((campaigns ?? []).map((c) => c.proposedBy));
  const seq = useRef(0);

  const refusal = useCallback(
    (e: { code: string; reasonCode?: string; detail?: string }): string => (e.code === 'FORBIDDEN' && e.reasonCode === undefined ? t('ref.refusal.forbidden') : t('ref.error', { code: fail(e) })),
    [t, fail],
  );
  const failText = useCallback((e: { code: string; reasonCode?: string; detail?: string }): string => (e.reasonCode !== undefined || e.detail !== undefined ? fail(e) : refusal(e)), [fail, refusal]);

  const load = useCallback(
    async (m: string): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      if (target === '' || !mayView) {
        setLoaded(target);
        setCampaigns(undefined);
        return;
      }
      const [drivers, riders, catalog] = await Promise.all([runtime.driverReferrals.list(target), runtime.referralCampaigns.campaigns(target), runtime.serviceCategories.market(target)]);
      if (mine !== seq.current) return;
      setLoaded(target);
      if (!drivers.ok) return setError(refusal(drivers.error));
      if (!riders.ok) return setError(refusal(riders.error));
      setError(undefined);
      setCeiling(drivers.value.ceiling);
      setCampaigns(drivers.value.campaigns);
      setRiderCampaigns(riders.value.campaigns);
      // The category list is the market's own catalog (ACTIVE mobility categories) - never a typed code.
      if (catalog.ok) {
        setCategories(catalog.value.categories.filter((c) => c.status === 'ACTIVE' && c.kind === 'mobility'));
        setCategoriesFailed(false);
      } else setCategoriesFailed(true);
    },
    [runtime, mayView, refusal],
  );

  useEffect(() => {
    setCampaigns(undefined);
    setRiderCampaigns(undefined);
    setCeiling(undefined);
    setError(undefined);
    setDraft(EMPTY_DRIVER_REFERRAL_DRAFT);
    setRiderDraft(EMPTY_RIDER_REFERRAL_DRAFT);
    void load(market);
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market), { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && market !== '' && !busy });

  const tz = marketTimeZone(loaded, markets);
  const when = (iso: string | undefined): string => (iso === undefined ? '—' : formatMarketInstant(iso, tz, lang));
  const currency = ceiling?.currency ?? '';
  const money = useCallback(
    (minor: string | bigint | undefined, cur: string = currency): string => {
      const value = typeof minor === 'bigint' ? minor : minor !== undefined && /^\d+$/.test(minor) ? BigInt(minor) : undefined;
      return value === undefined || !isIso4217Currency(cur) ? '—' : formatMoney({ amountMinor: value, currency: cur }, { locale: lang, timeZone: tz ?? 'UTC', measurement: 'metric' });
    },
    [currency, lang, tz],
  );
  const categoryCodes = useMemo(() => (categories ?? []).map((c) => c.code), [categories]);
  const labelOf = (code: string): string => categoryLabel((categories ?? []).find((c) => c.code === code), code, lang);
  const ceilingOn = ceiling !== undefined && ceiling.enabled && ceiling.couponsEnabled && isIso4217Currency(currency);
  const issue = ceiling !== undefined ? driverReferralDraftIssue(draft, ceiling, categoryCodes) : undefined;
  const covered = rewardsCovered(draft);
  const face = draftFaceMinor(draft);

  const act = async (run: () => Promise<{ ok: boolean; error?: { code: string; reasonCode?: string; detail?: string } }>, done: string): Promise<boolean> => {
    setBusy(true);
    const res = await run();
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: res.error !== undefined ? failText(res.error) : t('ref.refusal.forbidden') });
      await load(loaded);
      return false;
    }
    setMsg({ tone: 'success', text: done });
    await load(loaded);
    return true;
  };

  const propose = async (): Promise<void> => {
    if (issue !== undefined || !ceilingOn) return;
    const ok = await act(() => runtime.driverReferrals.propose(driverReferralProposalBody(loaded, draft, currency, new Date().toISOString())), t('ref.propose.sent'));
    if (ok) setDraft(EMPTY_DRIVER_REFERRAL_DRAFT);
  };
  const decide = (c: DriverReferralCampaignView, approve: boolean): Promise<boolean> => {
    const text = (notes[c.campaignId] ?? '').trim();
    return act(() => runtime.driverReferrals.decide(loaded, c.campaignId, approve, text !== '' ? text : undefined), t(approve ? 'ref.decision.approved' : 'ref.decision.refused'));
  };
  const proposeChange = (c: DriverReferralCampaignView): Promise<boolean> => {
    const ch = changes[c.campaignId] ?? {};
    const days = ch.days !== undefined ? intOf(ch.days) : undefined;
    const cap = ch.cap !== undefined ? intOf(ch.cap) : undefined;
    const body = {
      ...(days !== undefined ? { validTo: new Date(Date.now() + days * 86_400_000).toISOString() } : {}),
      ...(cap !== undefined ? { perReferrerCap: cap } : {}),
      ...(ch.budget !== undefined ? { budgetMinor: ch.budget.toString() } : {}),
    };
    return act(() => runtime.driverReferrals.proposeChange(loaded, c.campaignId, body), t('ref.change.sent'));
  };
  const pendingText = (c: DriverReferralCampaignView): string => {
    const p = c.pendingChange;
    if (p === undefined) return '';
    return [
      p.validTo !== undefined ? t('ref.change.pending.validTo', { when: when(p.validTo) }) : '',
      p.budgetMinor !== undefined ? t('ref.change.pending.budget', { amount: money(p.budgetMinor, c.reward.currency) }) : '',
      p.perReferrerCap !== undefined ? t('ref.change.pending.cap', { cap: p.perReferrerCap }) : '',
    ].filter((x) => x !== '').join(' · ');
  };

  const actionCell = (c: DriverReferralCampaignView): ReactElement => {
    const block = driverCampaignDecisionBlock(c, me?.id, mayManage, selfApproval);
    if (block === undefined) {
      return (
        <Stack gap={2}>
          <TextInput aria-label={t('ref.decision.note')} placeholder={t('ref.decision.note')} value={notes[c.campaignId] ?? ''} maxLength={500} onChange={(ev) => setNotes((r) => ({ ...r, [c.campaignId]: ev.target.value }))} />
          <Row gap={2} align="center" wrap>
            <Button type="button" variant="success" disabled={busy} onClick={() => void decide(c, true)}>{t('ref.decision.approve')}</Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => void decide(c, false)}>{t('ref.decision.refuse')}</Button>
          </Row>
        </Stack>
      );
    }
    if (block === 'OWN_PROPOSAL' || block === 'NO_PERMISSION') return <Text role="caption" tone="tertiary">{block === 'OWN_PROPOSAL' ? t('ref.approve.own') : t('ref.approve.noPermission')}</Text>;
    if (c.pendingChange !== undefined) {
      const changeBlock = driverChangeDecisionBlock(c, me?.id, mayManage, selfApproval);
      return (
        <Stack gap={2}>
          <Text role="caption" tone="tertiary"><span className="tnum">{t('ref.change.pending', { what: pendingText(c) })}</span></Text>
          {changeBlock === undefined ? (
            <Row gap={2} align="center" wrap>
              <Button type="button" variant="success" disabled={busy} onClick={() => void act(() => runtime.driverReferrals.decideChange(loaded, c.campaignId, true), t('ref.change.done'))}>{t('ref.change.approve')}</Button>
              <Button type="button" variant="danger" disabled={busy} onClick={() => void act(() => runtime.driverReferrals.decideChange(loaded, c.campaignId, false), t('ref.change.done'))}>{t('ref.change.refuse')}</Button>
            </Row>
          ) : changeBlock === 'OWN_PROPOSAL' ? (
            <Text role="caption" tone="tertiary">{t('ref.approve.own')}</Text>
          ) : null}
        </Stack>
      );
    }
    if (driverCampaignCanChange(c, mayManage)) {
      const ch = changes[c.campaignId] ?? {};
      return (
        <Stack gap={2}>
          <TextInput aria-label={t('ref.change.validTo')} placeholder={t('ref.change.validTo')} inputMode="numeric" value={ch.days ?? ''} maxLength={4} onChange={(ev) => setChanges((r) => ({ ...r, [c.campaignId]: { ...ch, days: ev.target.value } }))} />
          <TextInput aria-label={t('ref.change.cap')} placeholder={t('ref.change.cap')} inputMode="numeric" value={ch.cap ?? ''} maxLength={4} onChange={(ev) => setChanges((r) => ({ ...r, [c.campaignId]: { ...ch, cap: ev.target.value } }))} />
          {isIso4217Currency(c.reward.currency) && (
            <MoneyInput id={`ref-change-budget-${c.campaignId}`} label={t('ref.change.budget')} currency={c.reward.currency} locale={lang} valueMinor={ch.budget} onChangeMinor={(minor) => setChanges((r) => ({ ...r, [c.campaignId]: { ...ch, ...(minor !== undefined ? { budget: minor } : {}) } }))} issueText={(i) => v('format.money_input_issue', i)} />
          )}
          <Row gap={2} align="center" wrap>
            <Button type="button" variant="secondary" disabled={busy || (ch.days === undefined && ch.cap === undefined && ch.budget === undefined)} onClick={() => void proposeChange(c)}>{t('ref.change.submit')}</Button>
            {driverCampaignCanEnd(c, mayManage) && <Button type="button" variant="danger" disabled={busy} onClick={() => void act(() => runtime.driverReferrals.end(loaded, c.campaignId), t('ref.end.done'))}>{t('ref.end.button')}</Button>}
          </Row>
        </Stack>
      );
    }
    return <span>{c.decisionNote ?? '—'}</span>;
  };

  const riderToggle = (c: ReferralCampaignView): Promise<boolean> => act(() => runtime.referralCampaigns.adjustCampaign(loaded, c.campaignId, { active: !c.active }), t('ref.rider.updated'));
  const riderExtend = async (c: ReferralCampaignView): Promise<void> => {
    const validTo = riderDeadlineAfter(intOf(riderDays[c.campaignId] ?? ''), new Date().toISOString());
    if (validTo === undefined) return;
    const ok = await act(() => runtime.referralCampaigns.adjustCampaign(loaded, c.campaignId, { validTo }), t('ref.rider.extend.done'));
    if (ok) setRiderDays((r) => ({ ...r, [c.campaignId]: '' }));
  };
  const riderAction = (c: ReferralCampaignView): ReactElement => {
    if (!mayManage) return <span>—</span>;
    const days = riderDays[c.campaignId] ?? '';
    return (
      <Stack gap={2}>
        <TextInput aria-label={t('ref.rider.extend.days')} placeholder={t('ref.rider.extend.days')} inputMode="numeric" value={days} maxLength={4} onChange={(ev) => setRiderDays((r) => ({ ...r, [c.campaignId]: ev.target.value }))} />
        <Row gap={2} align="center" wrap>
          <Button type="button" variant="secondary" disabled={busy || riderDeadlineAfter(intOf(days), new Date().toISOString()) === undefined} onClick={() => void riderExtend(c)}>{t('ref.rider.extend.submit')}</Button>
          <Button type="button" variant={c.active ? 'danger' : 'secondary'} disabled={busy} onClick={() => void riderToggle(c)}>{c.active ? t('ref.rider.turnOff') : t('ref.rider.turnOn')}</Button>
        </Row>
      </Stack>
    );
  };
  const createRider = async (): Promise<void> => {
    if (!riderReferralDraftComplete(riderDraft) || !isIso4217Currency(currency)) return;
    const ok = await act(() => runtime.referralCampaigns.createCampaign(riderReferralBody(loaded, riderDraft, currency, new Date().toISOString()) as never), t('ref.rider.create.done'));
    if (ok) setRiderDraft(EMPTY_RIDER_REFERRAL_DRAFT);
  };

  return (
    <>
      <PageHeader title={t('ref.title')} subtitle={t('ref.subtitle')} />
      {!mayView ? (
        <Panel>{note(t('ref.noPermission'))}</Panel>
      ) : (
        <>
          {(error !== undefined || msg !== undefined) && (
            <Panel>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            </Panel>
          )}

          {scopeMarket === undefined && <ScopeMarketNotice choices={choices} />}

          {ceiling !== undefined && campaigns !== undefined && (
            <>
              <Panel>
                <Stack gap={3}>
                  <h3>{t('ref.ceiling.title')}</h3>
                  {!ceilingOn ? (
                    <Notice tone="warning">{t('ref.ceiling.off')}</Notice>
                  ) : (
                    <Text role="caption" tone="tertiary">
                      <span className="tnum">
                        {t('ref.ceiling.bounds', {
                          budget: money(ceiling.maxBudgetMinor), rides: ceiling.maxRidesRequired, window: ceiling.maxCompletionWindowDays, days: ceiling.maxCampaignDays, cap: ceiling.maxPerReferrerCap, attribution: ceiling.attributionWindowDays,
                        })}
                      </span>
                    </Text>
                  )}
                  <Text role="caption" tone="tertiary">{v('settings.reason.driver_referral', ceiling.reasonCode)} · {t('ref.ceiling.version', { version: ceiling.policyVersion })}</Text>
                </Stack>
              </Panel>

              <Panel padded={false}>
                <Stack gap={3}>
                  <h3>{t('ref.driver.title')}</h3>
                  <Table
                    columns={[
                      {
                        key: 'campaign', header: t('ref.col.campaign'),
                        render: (c: DriverReferralCampaignView) => (
                          <Stack gap={1}>
                            <span>{c.name}</span>
                            <Text role="caption" tone="tertiary">
                              <span className="tnum">
                                {c.reward.discountKind === 'PERCENT'
                                  ? t('ref.terms.percent', { percent: referralPercentLabel(c.reward.percentBasisPoints ?? 0), max: money(c.reward.maxDiscountMinor, c.reward.currency), uses: c.reward.uses, validDays: c.reward.validDays, rides: c.ridesRequired, window: c.completionWindowDays })
                                  : t('ref.terms', { amount: money(c.reward.fixedMinor, c.reward.currency), uses: c.reward.uses, validDays: c.reward.validDays, rides: c.ridesRequired, window: c.completionWindowDays })}
                              </span>
                            </Text>
                            <Text role="caption" tone="tertiary"><span className="tnum">{t('ref.category', { category: labelOf(c.categoryCode), cap: c.perReferrerCap })}</span></Text>
                          </Stack>
                        ),
                      },
                      { key: 'window', header: t('ref.col.window'), render: (c: DriverReferralCampaignView) => <span className="tnum">{t('ref.window.line', { from: when(c.validFrom), to: when(c.endedAt ?? c.validTo) })}</span> },
                      {
                        key: 'budget', header: t('ref.col.budget'), numeric: true,
                        render: (c: DriverReferralCampaignView) => (
                          <Stack gap={1}>
                            <Meter label={t('ref.col.budget')} value={committedBp(c)} max={10_000} showMax={false} warnAt={0.8} dangerAt={1} formatValue={(x) => `${Math.floor(x / 100)}%`} />
                            <Text role="caption" tone="tertiary">
                              <span className="tnum">{t('ref.budget.line', { committed: money(c.committedMinor ?? '0', c.reward.currency), budget: money(c.budgetMinor, c.reward.currency), open: c.open ?? 0, rewarded: c.rewarded ?? 0, expired: c.expired ?? 0 })}</span>
                            </Text>
                          </Stack>
                        ),
                      },
                      { key: 'state', header: t('ref.col.state'), render: (c: DriverReferralCampaignView) => <Badge tone={driverCampaignTone(c.status)}>{v('promotions.driver_referral_campaign_status', c.status)}</Badge> },
                      { key: 'by', header: t('ref.col.by'), render: (c: DriverReferralCampaignView) => <span>{t('ref.by.line', { who: personName(c.proposedBy) ?? tailOf(c.proposedBy), when: when(c.proposedAt) })}</span> },
                      { key: 'action', header: t('ref.col.action'), render: actionCell },
                    ]}
                    rows={campaigns}
                    rowKey={(c) => c.campaignId}
                    empty={note(t('ref.driver.empty'))}
                  />
                </Stack>
              </Panel>

              {mayManage && ceilingOn && (
                <Panel>
                  <Stack gap={3}>
                    <h3>{t('ref.propose.title')}</h3>
                    {categoriesFailed && <Notice tone="warning">{t('ref.propose.categoriesError')}</Notice>}
                    <FormRow action={<Button type="button" disabled={issue !== undefined || busy} onClick={() => void propose()}>{t('ref.propose.submit')}</Button>}>
                      <Field label={t('ref.propose.category')} htmlFor="ref-category">
                        <Select id="ref-category" value={draft.categoryCode} onChange={(ev) => setDraft((d) => ({ ...d, categoryCode: ev.target.value }))}>
                          <option value="">{t('ref.propose.choose')}</option>
                          {(categories ?? []).map((c) => (
                            <option key={c.code} value={c.code}>{categoryLabel(c, c.code, lang)}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={t('ref.propose.name')} htmlFor="ref-name" hint={t('ref.propose.name.hint')}>
                        <TextInput id="ref-name" value={draft.name} maxLength={80} onChange={(ev) => setDraft((d) => ({ ...d, name: ev.target.value }))} />
                      </Field>
                      <Field label={t('ref.propose.days')} htmlFor="ref-days" hint={t('ref.propose.max', { max: ceiling.maxCampaignDays })}>
                        <TextInput id="ref-days" inputMode="numeric" maxLength={4} value={draft.campaignDays === undefined ? '' : String(draft.campaignDays)} onChange={(ev) => setDraft((d) => ({ ...d, campaignDays: intOf(ev.target.value) }))} />
                      </Field>
                      <Field label={t('ref.propose.rides')} htmlFor="ref-rides" hint={t('ref.propose.max', { max: ceiling.maxRidesRequired })}>
                        <TextInput id="ref-rides" inputMode="numeric" maxLength={5} value={draft.ridesRequired === undefined ? '' : String(draft.ridesRequired)} onChange={(ev) => setDraft((d) => ({ ...d, ridesRequired: intOf(ev.target.value) }))} />
                      </Field>
                      <Field label={t('ref.propose.window')} htmlFor="ref-window" hint={t('ref.propose.max', { max: ceiling.maxCompletionWindowDays })}>
                        <TextInput id="ref-window" inputMode="numeric" maxLength={4} value={draft.completionWindowDays === undefined ? '' : String(draft.completionWindowDays)} onChange={(ev) => setDraft((d) => ({ ...d, completionWindowDays: intOf(ev.target.value) }))} />
                      </Field>
                      <MoneyInput id="ref-reward" label={t('ref.propose.reward')} currency={currency} locale={lang} valueMinor={draft.rewardMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, rewardMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                      <Field label={t('ref.propose.uses')} htmlFor="ref-uses">
                        <TextInput id="ref-uses" inputMode="numeric" maxLength={3} value={draft.uses === undefined ? '' : String(draft.uses)} onChange={(ev) => setDraft((d) => ({ ...d, uses: intOf(ev.target.value) }))} />
                      </Field>
                      <Field label={t('ref.propose.validDays')} htmlFor="ref-valid">
                        <TextInput id="ref-valid" inputMode="numeric" maxLength={3} value={draft.validDays === undefined ? '' : String(draft.validDays)} onChange={(ev) => setDraft((d) => ({ ...d, validDays: intOf(ev.target.value) }))} />
                      </Field>
                      <MoneyInput id="ref-budget" label={t('ref.propose.budget')} currency={currency} locale={lang} valueMinor={draft.budgetMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, budgetMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                      <Field label={t('ref.propose.cap')} htmlFor="ref-cap" hint={t('ref.propose.max', { max: ceiling.maxPerReferrerCap })}>
                        <TextInput id="ref-cap" inputMode="numeric" maxLength={4} value={draft.perReferrerCap === undefined ? '' : String(draft.perReferrerCap)} onChange={(ev) => setDraft((d) => ({ ...d, perReferrerCap: intOf(ev.target.value) }))} />
                      </Field>
                    </FormRow>
                    {covered !== undefined && face !== undefined && <Text role="caption" tone="tertiary"><span className="tnum">{t('ref.propose.preview', { count: covered, face: money(face) })}</span></Text>}
                    {issue !== undefined && draft !== EMPTY_DRIVER_REFERRAL_DRAFT && <Text role="caption" tone="tertiary">{t(`ref.issue.${issue}`)}</Text>}
                  </Stack>
                </Panel>
              )}
            </>
          )}

          {riderCampaigns !== undefined && (
            <Panel padded={false}>
              <Stack gap={3}>
                <h3>{t('ref.rider.title')}</h3>
                <Text role="caption" tone="tertiary">{t('ref.rider.oneOperator')}</Text>
                <Table
                  columns={[
                    {
                      key: 'campaign', header: t('ref.col.campaign'),
                      render: (c: ReferralCampaignView) => (
                        <Stack gap={1}>
                          <span>{c.name}</span>
                          <Text role="caption" tone="tertiary">
                            <span className="tnum">
                              {t('ref.rider.terms', { referrer: money(c.referrerBenefit.fixedMinor, c.referrerBenefit.currency), referee: money(c.refereeBenefit.fixedMinor, c.refereeBenefit.currency), rides: c.ridesRequired, validDays: c.referrerBenefit.validDays })}
                            </span>
                          </Text>
                          <Text role="caption" tone="tertiary"><span className="tnum">{t('ref.rider.count', { count: c.referrals ?? 0 })}</span></Text>
                        </Stack>
                      ),
                    },
                    { key: 'window', header: t('ref.col.window'), render: (c: ReferralCampaignView) => <span className="tnum">{t('ref.window.line', { from: when(c.validFrom), to: when(c.validTo) })}</span> },
                    { key: 'state', header: t('ref.col.state'), render: (c: ReferralCampaignView) => <Badge tone={c.active ? 'success' : 'neutral'}>{c.active ? t('ref.rider.active') : t('ref.rider.inactive')}</Badge> },
                    { key: 'action', header: t('ref.col.action'), render: riderAction },
                  ]}
                  rows={riderCampaigns}
                  rowKey={(c) => c.campaignId}
                  empty={note(t('ref.rider.empty'))}
                />
                {mayManage && isIso4217Currency(currency) && (
                  <Stack gap={3}>
                    <h3>{t('ref.rider.create.title')}</h3>
                    <FormRow action={<Button type="button" variant="secondary" disabled={!riderReferralDraftComplete(riderDraft) || busy} onClick={() => void createRider()}>{t('ref.rider.create.submit')}</Button>}>
                      <Field label={t('ref.rider.create.name')} htmlFor="rref-name">
                        <TextInput id="rref-name" value={riderDraft.name} maxLength={80} onChange={(ev) => setRiderDraft((d) => ({ ...d, name: ev.target.value }))} />
                      </Field>
                      <Field label={t('ref.rider.create.days')} htmlFor="rref-days">
                        <TextInput id="rref-days" inputMode="numeric" maxLength={4} value={riderDraft.campaignDays === undefined ? '' : String(riderDraft.campaignDays)} onChange={(ev) => setRiderDraft((d) => ({ ...d, campaignDays: intOf(ev.target.value) }))} />
                      </Field>
                      <Field label={t('ref.rider.create.rides')} htmlFor="rref-rides">
                        <TextInput id="rref-rides" inputMode="numeric" maxLength={3} value={riderDraft.ridesRequired === undefined ? '' : String(riderDraft.ridesRequired)} onChange={(ev) => setRiderDraft((d) => ({ ...d, ridesRequired: intOf(ev.target.value) }))} />
                      </Field>
                      <Field label={t('ref.rider.create.newUser')} htmlFor="rref-new">
                        <TextInput id="rref-new" inputMode="numeric" maxLength={3} value={riderDraft.newUserWindowDays === undefined ? '' : String(riderDraft.newUserWindowDays)} onChange={(ev) => setRiderDraft((d) => ({ ...d, newUserWindowDays: intOf(ev.target.value) }))} />
                      </Field>
                      <MoneyInput id="rref-referrer" label={t('ref.rider.create.referrer')} currency={currency} locale={lang} valueMinor={riderDraft.referrerMinor} onChangeMinor={(minor) => setRiderDraft((d) => ({ ...d, referrerMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                      <MoneyInput id="rref-referee" label={t('ref.rider.create.referee')} currency={currency} locale={lang} valueMinor={riderDraft.refereeMinor} onChangeMinor={(minor) => setRiderDraft((d) => ({ ...d, refereeMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                      <Field label={t('ref.rider.create.validDays')} htmlFor="rref-valid">
                        <TextInput id="rref-valid" inputMode="numeric" maxLength={3} value={riderDraft.validDays === undefined ? '' : String(riderDraft.validDays)} onChange={(ev) => setRiderDraft((d) => ({ ...d, validDays: intOf(ev.target.value) }))} />
                      </Field>
                    </FormRow>
                  </Stack>
                )}
              </Stack>
            </Panel>
          )}
        </>
      )}
    </>
  );
}
